import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import ReceiptUpload from './ReceiptUpload';

const CATEGORIES = [
    'Travel',
    'Meals & Entertainment',
    'Office Supplies',
    'Accommodation',
    'Transportation',
    'Communication',
    'Other'
];

const CURRENCIES = [
    'USD', 'EUR', 'GBP', 'INR', 'JPY', 'AUD', 'CAD', 'SGD', 'CHF', 'AED', 'CNY', 'HKD', 'NZD', 'SEK', 'NOK', 'DKK', 'ZAR', 'BRL'
];

const ExpenseItemForm = ({ item, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        category: '',
        merchant: '',
        amount: '',
        currency: 'USD',
        date: new Date().toISOString().split('T')[0],
        description: '',
        receiptUrl: '',
        ...item
    });

    const [isSaving, setIsSaving] = useState(false);

    const [baseCurrency, setBaseCurrency] = useState('INR');
    const [rates, setRates] = useState({});

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await api.get('/auth/config');
                const base = res.data.baseCurrency || 'INR';
                setBaseCurrency(base);

                // Fetch rates relative to base
                const ratesRes = await fetch(`https://api.exchangerate-api.com/v4/latest/${base}`);
                const ratesData = await ratesRes.json();
                setRates(ratesData.rates || {});
            } catch (err) {
                console.error('Failed to fetch rates for preview', err);
            }
        };
        fetchConfig();
    }, []);

    const convertedAmount = useMemo(() => {
        if (!formData.amount || !formData.currency || !baseCurrency) return null;
        if (formData.currency === baseCurrency) return null;

        const rate = rates[formData.currency];
        if (!rate) return null;

        // rate is base/currency, so amount / rate gives base
        return (parseFloat(formData.amount) / rate).toFixed(2);
    }, [formData.amount, formData.currency, baseCurrency, rates]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleReceiptProcessed = (ocrData) => {
        setFormData(prev => ({
            ...prev,
            merchant: ocrData.merchant || prev.merchant,
            amount: ocrData.amount || prev.amount,
            date: ocrData.date || prev.date,
            currency: ocrData.currency || prev.currency,
            receiptUrl: ocrData.receiptUrl
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.merchant || !formData.amount) {
            alert('Please fill in merchant and amount');
            return;
        }
        
        setIsSaving(true);
        try {
            await onSave({
                ...formData,
                convertedAmount: convertedAmount || formData.amount,
                baseCurrency: baseCurrency
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
        }}>
            <div style={{
                background: 'white',
                borderRadius: 'var(--radius)',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: 'var(--shadow)'
            }}>
                <div style={{
                    padding: '1.5rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{ margin: 0 }}>{item ? 'Edit Expense' : 'Add Expense'}</h2>
                    <button
                        onClick={onCancel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                    <ReceiptUpload onReceiptProcessed={handleReceiptProcessed} />

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Category *
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            required
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        >
                            <option value="">Select category</option>
                            {CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Merchant *
                        </label>
                        <input
                            type="text"
                            value={formData.merchant}
                            onChange={(e) => handleChange('merchant', e.target.value)}
                            required
                            placeholder="e.g., Delta Airlines"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Amount *
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => handleChange('amount', e.target.value)}
                                required
                                placeholder="0.00"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Currency
                            </label>
                            <select
                                value={formData.currency}
                                onChange={(e) => handleChange('currency', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    fontSize: '1rem'
                                }}
                            >
                                {CURRENCIES.map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {convertedAmount && (
                        <div style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: 'var(--radius)',
                            color: '#1e40af',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.9rem'
                        }}>
                            <RefreshCw size={16} />
                            <span>Approx. <strong>{baseCurrency} {convertedAmount}</strong></span>
                            <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>(1 {baseCurrency} ≈ {rates[formData.currency]?.toFixed(4)} {formData.currency})</span>
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Date *
                        </label>
                        <input
                            type="date"
                            value={formData.date}
                            onChange={(e) => handleChange('date', e.target.value)}
                            required
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Optional notes about this expense"
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                fontSize: '1rem',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSaving}
                            className="btn btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn btn-primary"
                        >
                            <Save size={16} style={{ marginRight: '0.5rem' }} />
                            {isSaving ? 'Saving...' : 'Save Expense'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExpenseItemForm;
